import React from 'react'
import axios from 'axios'
import PromisePool from '@supercharge/promise-pool'
import {
    Grid,
    Card,
    Tabs,
    Button,
    Alert,
    Icon,
} from '@softwaredev_cegeka/stix-core'
import { Label } from '@softwaredev_cegeka/stix-form'
import { FileReaderInput } from 'src/components/FileReaderInput'
import { parseCSV } from 'src/utils/validation'
import AddUsersToGroupTable from 'src/components/EntityTable/AddUsersToGroupTable'
import './userImportWizard.scss'
import { notifierStore } from 'src/stores'
import {
    SimpleStatusBar,
    StatusBar,
} from '@softwaredev_cegeka/stix-core/dist/components'

class UserImportWizard extends React.Component {
    UPLOAD_TAB = 'upload'
    CREATE_TAB = 'create'

    state = {
        activeTab: this.UPLOAD_TAB,
        uploadedData: [],
        isValidFile: true,
        feedbackFile: '',
        creatingUsers: false,
        allowedEmailDomains: [],
        usersCreated: {
            notProcessed: 0,
            successful: 0,
            failed: 0,
            existing: 0,
        },
        creatingGroups: false,
        createdGroups: {
            notProcessed: 0,
            successful: 0,
            failed: 0,
        },
        disableFinishButton: false,
        failures: [],
    }

    componentDidMount() {
        this.getTenantEmailDomains()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.organizationId !== this.props.organizationId) {
            this.getTenantEmailDomains()
        }
    }

    getTenantEmailDomains() {
        if (this.props.organizationId)
            axios
                .get(
                    `/api/auth/tenants/${this.props.organizationId}/email_domains`
                )
                .then(res => {
                    let allowedEmailDomains = []
                    if (res.data?.email_domains?.length) {
                        allowedEmailDomains = res.data?.email_domains.map(
                            emailDomain => emailDomain.toLowerCase()
                        )
                    }
                    this.setState({
                        allowedEmailDomains: allowedEmailDomains,
                    })
                })
    }

    renderNavigation() {
        return (
            <div className={'navigation-buttons'}>
                <Grid.Row>
                    <Button
                        variant={'outline-secondary'}
                        disabled={this.state.activeTab === this.UPLOAD_TAB}
                        onClick={() =>
                            this.setState({ activeTab: this.UPLOAD_TAB })
                        }
                    >
                        Previous step
                    </Button>
                    {this.state.activeTab === this.UPLOAD_TAB ? (
                        <Button
                            variant={'secondary'}
                            disabled={!this.state.uploadedData.length}
                            onClick={() =>
                                this.setState({ activeTab: this.CREATE_TAB })
                            }
                        >
                            Next step
                        </Button>
                    ) : (
                        <Button
                            variant={'secondary'}
                            disabled={
                                (!this.state.uploadedData.every(
                                    user => user.groups?.length > 0
                                ) &&
                                    !this.state.creatingUsers &&
                                    !this.state.creatingGroups) ||
                                this.state.disableFinishButton
                            }
                            onClick={this.handleCreateUsers}
                        >
                            Finish
                        </Button>
                    )}
                </Grid.Row>
            </div>
        )
    }

    handleUploadChange = event => {
        try {
            const res = parseCSV(event.target.value, [
                'first_name',
                'last_name',
                'email',
            ])
            if (res.error) {
                this.setState({
                    isValidFile: false,
                    feedbackFile: res.error,
                    uploadedData: [],
                })
            } else {
                const { allowedEmailDomains } = this.state
                if (
                    res.data
                        .map(item => item.email)
                        .some(
                            // check if the email does NOT have an allowed email domain
                            email =>
                                !allowedEmailDomains.some(allowedEmail =>
                                    email.toLowerCase().includes(allowedEmail)
                                )
                        )
                ) {
                    this.setState({
                        isValidFile: false,
                        feedbackFile:
                            'Please make sure the users have their email address ' +
                            'in the list of allowed domains.',
                        uploadedData: [],
                    })
                } else {
                    // all emails are valid
                    this.setState({
                        isValidFile: true,
                        feedbackFile: '',
                        uploadedData: res.data.map(item => ({
                            ...item,
                            email: item.email.toLowerCase(),
                            groups: [],
                        })),
                    })
                }
            }
        } catch (e) {
            this.setState({
                isValidFile: false,
                feedbackFile: 'Please upload csv file',
                uploadedData: [],
            })
        }
    }

    renderImportUsers = () => {
        return (
            <Grid.Row>
                <Grid.Col md={6}>
                    <Label label={'Select file'} />
                    <FileReaderInput
                        isValid={this.state.isValidFile}
                        isInvalid={!this.state.isValidFile}
                        feedback={this.state.feedbackFile}
                        onChange={this.handleUploadChange}
                    />
                    {this.state.uploadedData.length > 0 &&
                        this.state.isValidFile && (
                            <Alert variant={'success'}>
                                File uploaded successfully
                            </Alert>
                        )}
                </Grid.Col>
                <Grid.Col md={6}>
                    <div className={'info-card'}>
                        <Icon icon={'info'} title={'INFO'} />
                        <div className={'info-card-body'}>
                            <p>Only csv files are allowed.</p>
                            <p>
                                The allowed delimiters are comma (,) or
                                semi-colon (;).
                            </p>
                            <p>
                                The file needs a header with the following
                                columns: first_name, last_name, email.
                            </p>
                            <p>
                                Users will be created based on their email. If
                                another user exists with the same email, that
                                user will have their groups updated. The
                                creation and group linking will happen when you
                                click Finish.
                            </p>
                            <p>
                                The users will be created under the currently
                                selected tenant.
                            </p>
                            {this.state.allowedEmailDomains.length ? (
                                <p>
                                    <strong>Allowed email domains: </strong>
                                    {this.state.allowedEmailDomains.join(', ')}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </Grid.Col>
            </Grid.Row>
        )
    }

    handleUpdateUsers = users => {
        const { uploadedData } = this.state
        const usersMapping = {}
        uploadedData.forEach(item => (usersMapping[item.email] = item))
        users.forEach(user => {
            usersMapping[user.email] = user
        })
        this.setState({
            uploadedData: Object.values(usersMapping),
        })
    }

    createUsers = async users => {
        return await PromisePool.withConcurrency(10)
            .for(users)
            .process(async user => {
                try {
                    const result = await axios.post('/api/auth/users', {
                        first_name: user.first_name,
                        last_name: user.last_name,
                        username: user.email,
                        email: user.email,
                        is_global_admin: false,
                        local_user: false,
                        locked: false,
                        tenant_uid: this.props.organizationId,
                    })
                    if (result.status === 200) {
                        this.setState((state, props) => ({
                            usersCreated: {
                                ...state.usersCreated,
                                successful: state.usersCreated.successful + 1,
                                notProcessed:
                                    state.usersCreated.notProcessed - 1,
                            },
                        }))
                        return {
                            ...user,
                            id: result.data?.id,
                        }
                    }
                } catch (e) {
                    try {
                        const result = await axios.get(
                            `/api/auth/users/${user.email}`
                        )
                        if (result.status === 200) {
                            this.setState((state, props) => ({
                                usersCreated: {
                                    ...state.usersCreated,
                                    existing: state.usersCreated.existing + 1,
                                    notProcessed:
                                        state.usersCreated.notProcessed - 1,
                                },
                            }))
                            return { ...user, id: result.data?.id }
                        }
                    } catch (e) {
                        console.warn(
                            `Could not find user ${user.email} but creation failed`
                        )
                        this.setState((state, props) => {
                            return {
                                usersCreated: {
                                    ...state.usersCreated,
                                    failed: state.usersCreated.failed + 1,
                                    notProcessed:
                                        state.usersCreated.notProcessed - 1,
                                },
                                failures: [
                                    `Could not find user ${user.email} but creation failed`,
                                    ...state.failures,
                                ],
                            }
                        })
                        return {
                            ...user,
                            id: null,
                        }
                    }
                }
            })
    }

    addUsersToGroups = async (groups, groupMappings) => {
        const maxUsers = 100
        return await PromisePool.withConcurrency(10)
            .for(Object.entries(groups))
            .process(async ([groupId, userIds]) => {
                try {
                    const promises = []
                    for (let i = 0; i < userIds.length; i += maxUsers) {
                        promises.push(
                            axios.post(`/api/auth/groups/${groupId}/users`, {
                                ids: userIds.slice(i, i + maxUsers),
                            })
                        )
                    }
                    await Promise.all(promises)
                    this.setState((state, props) => ({
                        createdGroups: {
                            ...state.createdGroups,
                            successful: state.createdGroups.successful + 1,
                            notProcessed: state.createdGroups.notProcessed - 1,
                        },
                    }))
                } catch (e) {
                    console.warn(
                        'Could not add users to group ',
                        groupMappings[groupId]
                    )
                    this.setState((state, props) => ({
                        createdGroups: {
                            ...state.createdGroups,
                            failed: state.createdGroups.failed + 1,
                            notProcessed: state.createdGroups.notProcessed - 1,
                        },
                        failures: [
                            ...state.failures,
                            `Could not add users to group ${groupMappings[groupId]}`,
                        ],
                    }))
                }
            })
    }

    handleCreateUsers = async () => {
        this.setState(
            {
                creatingUsers: true,
                usersCreated: {
                    notProcessed: this.state.uploadedData.length,
                    successful: 0,
                    failed: 0,
                    existing: 0,
                },
            },
            async () => {
                const userResults = await this.createUsers(
                    this.state.uploadedData
                )
                const groups = {}
                const groupMappings = {}
                userResults.results.forEach(user => {
                    if (user.id) {
                        user.groups.forEach(group => {
                            if (groups[group.id]) {
                                groups[group.id].push(user.id)
                            } else {
                                groups[group.id] = [user.id]
                                groupMappings[group.id] = group.value
                            }
                        })
                    }
                })
                this.setState(
                    {
                        creatingGroups: true,
                        createdGroups: {
                            notProcessed: Object.entries(groups).length,
                            successful: 0,
                            failed: 0,
                        },
                        disableFinishButton: true,
                    },
                    async () => {
                        await this.addUsersToGroups(groups, groupMappings)
                        notifierStore.setToastInfo(
                            `Finished importing users.`,
                            true
                        )
                        notifierStore.setShowMessage(true)
                    }
                )
            }
        )
    }

    renderUserProgress = () => {
        const { notProcessed, successful, failed, existing } =
            this.state.usersCreated
        return this.state.creatingUsers ? (
            <StatusBar
                data={[
                    {
                        variant: 'danger',
                        count: failed,
                    },
                    {
                        variant: 'busy',
                        count: existing,
                    },
                    {
                        variant: 'success',
                        count: successful,
                    },
                    {
                        variant: 'gray',
                        count: notProcessed,
                    },
                ]}
                title={'User creation progress'}
            >
                <SimpleStatusBar
                    data={[
                        {
                            variant: 'danger',
                            count: `${failed} failures`,
                        },
                        {
                            variant: 'busy',
                            count: `${existing} existing users`,
                        },
                        {
                            variant: 'success',
                            count: `${successful} successfully created`,
                        },
                        {
                            variant: 'gray',
                            count: `${notProcessed} not processed`,
                        },
                    ]}
                />
            </StatusBar>
        ) : null
    }

    renderGroupProgress = () => {
        const { notProcessed, successful, failed } = this.state.createdGroups
        return this.state.creatingGroups ? (
            <StatusBar
                data={[
                    {
                        variant: 'danger',
                        count: failed,
                    },
                    {
                        variant: 'success',
                        count: successful,
                    },
                    {
                        variant: 'gray',
                        count: notProcessed,
                    },
                ]}
                title={'Adding users to groups progress'}
            >
                <SimpleStatusBar
                    data={[
                        {
                            variant: 'danger',
                            count: `${failed} group failures`,
                        },
                        {
                            variant: 'success',
                            count: `${successful} successfully processed groups`,
                        },
                        {
                            variant: 'gray',
                            count: `${notProcessed} not processed groups`,
                        },
                    ]}
                />
            </StatusBar>
        ) : null
    }

    renderCreateTab = () => {
        const { failures } = this.state
        return (
            <>
                <AddUsersToGroupTable
                    data={this.state.uploadedData}
                    onUpdateUsers={this.handleUpdateUsers}
                    organizationId={this.props.organizationId}
                />
                <div className={'progress-info'}>
                    {this.renderUserProgress()}
                </div>
                <div className={'progress-info'}>
                    {this.renderGroupProgress()}
                </div>
                {failures.length ? (
                    <Alert
                        variant={'danger'}
                        className={'errors-occurred-alert'}
                    >
                        <Alert.Heading>Errors occurred:</Alert.Heading>
                        {failures.map(failure => (
                            <p>{failure}</p>
                        ))}
                    </Alert>
                ) : null}
            </>
        )
    }

    render() {
        return (
            <Grid.Container>
                <div className={'page-header'}>
                    <h1 className="content-title">Bulk import: Users</h1>
                </div>
                <div className={'user-import-wizard'}>
                    <Card>
                        <Card.Body>
                            <Tabs
                                variant="tabs"
                                activeKey={this.state.activeTab}
                                onSelect={key =>
                                    this.setState({ activeTab: key })
                                }
                            >
                                <Tabs.Pane
                                    eventKey={this.UPLOAD_TAB}
                                    title="Import users"
                                >
                                    {this.renderImportUsers()}
                                </Tabs.Pane>
                                <Tabs.Pane
                                    eventKey={this.CREATE_TAB}
                                    title="Add users to group"
                                    disabled={!this.state.uploadedData.length}
                                >
                                    {this.renderCreateTab()}
                                </Tabs.Pane>
                            </Tabs>
                        </Card.Body>
                        <Card.Footer>{this.renderNavigation()}</Card.Footer>
                    </Card>
                </div>
            </Grid.Container>
        )
    }
}

export default UserImportWizard
